export const BASE = (import.meta.env.VITE_API_BASE as string | undefined) || "https://otp.mioe.com.tr";
// Derived brand/host text so logo titles etc. stay correct across deployments
// (this codebase runs at more than one live domain — the Cloudflare Worker and
// the single-machine box — see docs/AWS-ISTANBUL-SINGLE-MACHINE.md) without
// hardcoding a second literal that could drift from BASE.
export const SITE_HOST = new URL(BASE).host;

export interface Webapp {
  id: string;
  code: string;
  code_hidden: boolean;
  name: string;
  callback_url: string;
  inbound_number: string | null;
  has_webhook_key: boolean;
  webhook_url_template: string;
  active: boolean;
  verified_count: number;
  quota_allowance: number | null;
  quota_remaining: number | null;
  billing_status: "unlimited" | "subscribed" | "trial" | "trial_exhausted";
  created_at: number;
  updated_at: number;
}

export interface WebappSecrets {
  api_key: string;
  hmac_secret: string;
  webhook_url: string;
}

export interface AccountTrial {
  trial_quota: number;
  trial_used: number;
  trial_remaining: number;
}

export type CreateWebappResult =
  | { kind: "created"; webapp: Webapp & WebappSecrets }
  | { kind: "reactivatable"; existing: Webapp };

export interface CreditPackage {
  id: string;
  credits: number;
  net_kurus: number;
  kdv_kurus: number;
  gross_kurus: number;
}

export interface CreditSummary {
  trial_credits: number;
  paid_credits: number;
  consumed_credits: number;
  allowance: number;
  remaining: number;
  pricing: { price_per_credit_kurus: number; reference_price_per_sms_kurus: number };
  packages: CreditPackage[];
  iyzico_enabled: boolean;
}

export interface PublicPricing {
  price_per_credit_kurus: number;
  packages: CreditPackage[];
}

export interface CreditOrder {
  id: string;
  package_id: string;
  credits: number;
  price_kurus: number;
  currency: string;
  status: "pending" | "paid" | "failed";
  created_at: number;
  paid_at: number | null;
}

export interface Me {
  id: string;
  email: string;
  phone: string | null;
}

export interface SmsLoginInit {
  sessionId: string;
  // One-time token from the init call. Held in memory only (never stored,
  // never in a URL) and posted back to claim the verified session — the API
  // will not mint a cookie, or bind a phone, without it.
  pollToken: string;
  code: string;
  otp: string;
  inboundNumber?: string;
  expiresAt: string;
  expiresIn: number;
}

// Build a cross-platform "sms:" deep link prefilled with the body text.
// "?&body=" is the form both iOS and Android honor.
export function smsDeepLink(inboundNumber: string | undefined, body: string): string | null {
  if (!inboundNumber) return null;
  const number = inboundNumber.replace(/[^0-9+]/g, "");
  if (!number) return null;
  return `sms:${number}?&body=${encodeURIComponent(body)}`;
}

export type SmsLoginStatus =
  | { status: "pending" }
  | { status: "timeout" }
  | { status: "failed" }
  | { status: "no_account" }
  | { status: "email_unverified" }
  | { status: "verified"; id: string; email: string };

export type PhoneBindStatus =
  | { status: "pending" }
  | { status: "timeout" }
  | { status: "failed" }
  | { status: "phone_taken" }
  | { status: "verified"; phone: string };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg = (data && (data.error as string)) || `http_${res.status}`;
    throw new Error(msg);
  }
  return data as T;
}

export const api = {
  me: () => request<Me>("/customer/me"),
  signup: (email: string, password: string, acceptTerms: boolean) =>
    request<{ ok: true; email_verification_required: boolean }>("/customer/signup", {
      method: "POST",
      body: JSON.stringify({ email, password, accept_terms: acceptTerms }),
    }),
  verifyEmail: (token: string) =>
    request<{ id: string; email: string }>("/customer/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  resendVerification: (email: string) =>
    request<{ ok: true }>("/customer/resend-verification", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  login: (email: string, password: string) =>
    request<{ id: string; email: string }>("/customer/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>("/customer/logout", { method: "POST" }),

  smsLoginInit: (phone: string) =>
    request<SmsLoginInit>("/customer/login/sms/init", {
      method: "POST",
      body: JSON.stringify({ phone }),
    }),
  smsLoginStatus: (sessionId: string, pollToken: string) =>
    request<SmsLoginStatus>(`/customer/login/sms/status/${sessionId}`, {
      method: "POST",
      body: JSON.stringify({ pollToken }),
    }),

  requestPasswordReset: (email: string) =>
    request<{ ok: true }>("/customer/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  confirmPasswordReset: (token: string, password: string) =>
    request<{ ok: true }>("/customer/password-reset/confirm", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),

  phoneBindInit: (phone: string) =>
    request<SmsLoginInit>("/customer/phone/bind/init", {
      method: "POST",
      body: JSON.stringify({ phone }),
    }),
  phoneBindStatus: (sessionId: string, pollToken: string) =>
    request<PhoneBindStatus>(`/customer/phone/bind/status/${sessionId}`, {
      method: "POST",
      body: JSON.stringify({ pollToken }),
    }),
  unbindPhone: () => request<{ ok: true }>("/customer/phone", { method: "POST" }),

  exportMyData: () => request<Record<string, unknown>>("/customer/me/export"),
  deleteMyAccount: () =>
    request<{ ok: true; deleted: true }>("/customer/me/delete", { method: "POST" }),

  listWebapps: () => request<{ items: Webapp[]; account: AccountTrial }>("/customer/webapps"),
  // Create returns a discriminated result: a successful creation, or a
  // "reactivatable" signal when the chosen code belongs to a soft-deleted app
  // the customer owns (so the UI can offer to bring it back).
  createWebapp: async (body: {
    name: string;
    callback_url: string;
    // Empty/omitted when the customer uses their own SMS number/gateway — the
    // server then auto-generates a code (unused in that mode).
    code?: string;
  }): Promise<CreateWebappResult> => {
    const res = await fetch(`${BASE}/customer/webapps`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (res.ok) return { kind: "created", webapp: data as Webapp & WebappSecrets };
    if (res.status === 409 && data?.error === "exists_reactivatable") {
      return { kind: "reactivatable", existing: data.webapp as Webapp };
    }
    throw new Error((data && (data.error as string)) || `http_${res.status}`);
  },
  deleteWebapp: (id: string) =>
    request<{ ok: true }>(`/customer/webapps/${id}`, { method: "DELETE" }),
  reactivateWebapp: (id: string) =>
    request<Webapp>(`/customer/webapps/${id}/reactivate`, { method: "POST" }),
  updateWebapp: (
    id: string,
    body: Partial<Pick<Webapp, "name" | "callback_url" | "active">>,
  ) => request<Webapp>(`/customer/webapps/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  rotateApiKey: (id: string) =>
    request<{ id: string; api_key: string }>(`/customer/webapps/${id}/rotate-api-key`, {
      method: "POST",
    }),
  rotateWebhookKey: (id: string) =>
    request<{ id: string; webhook_url: string }>(`/customer/webapps/${id}/rotate-webhook-key`, {
      method: "POST",
    }),

  getPricing: () => request<PublicPricing>("/pricing"),
  getCredits: () => request<CreditSummary>("/customer/credits"),
  listCreditOrders: () => request<{ items: CreditOrder[] }>("/customer/credits/orders"),
  startCheckout: (packageId: string) =>
    request<{ order_id: string; token: string; payment_page_url: string }>(
      "/customer/credits/checkout",
      { method: "POST", body: JSON.stringify({ package_id: packageId }) },
    ),
};

// Format an integer kuruş amount as a Turkish-lira string, e.g. 3600 -> "36,00 ₺".
export function fmtTL(kurus: number): string {
  return (
    (kurus / 100).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " ₺"
  );
}

export function fmtTime(ms: number | null | undefined): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}
